import { proxy } from 'valtio'
import { deepClone, devtools } from 'valtio/utils'

import { resetStore } from '@/lib/valtio/reset-store'
import { useStoreKeySelector } from '@/lib/valtio/store-hooks'

import type { IndexedCue } from '~shared/utils'

import { getIndexedCue } from './cue.derived'

import type { Snapshot } from 'valtio'

/**
 * The dragged cue, frozen at drag start.
 *
 * A snapshot, not the live proxy: the drag operates on the cue as it was when
 * the pointer went down, and the value outlives a render.
 */
type DraggingCue = Snapshot<IndexedCue>

type CueDndState = {
  /** The cue being dragged, or null when no drag is in progress. */
  draggingCue: DraggingCue | null
}

const initialState: CueDndState = {
  draggingCue: null,
}

/**
 * Drag and drop state for the cue list.
 *
 * @remarks
 *   Kept separate from `gridStore`, which is about cells, selection and
 *   editing: this holds a single field with a lifetime of one pointer gesture,
 *   and every reader of it is a cue row or a dropzone.
 *   Separate from `cueUiStore` too, because nothing derives from it. Collapsed
 *   groups feed the derived cue list, a drag does not.
 */
const cueDndStore = proxy<CueDndState>(deepClone(initialState))

export const cueDndActions = {
  /**
   * Begin dragging a cue.
   *
   * Resolves the cue itself rather than taking it as an argument, so callers
   * only have to hand over the id that dnd-kit gives them. Reads once, without
   * subscribing, because the drag needs the cue as it was at pointer down.
   */
  startDrag: (cueId: string) => {
    const cue = getIndexedCue(cueId)
    if (!cue) return

    cueDndStore.draggingCue = cue
  },

  endDrag: () => {
    cueDndStore.draggingCue = null
  },

  /** Called when leaving a rundown. */
  resetStore: () => {
    resetStore(cueDndStore, initialState)
  },
}

/**
 * The store itself, for the store hooks and non-React readers.
 *
 * Read-only by type, so writes have to go through the actions above.
 */
export const cueDndStoreReadonly: Readonly<CueDndState> = cueDndStore

// =============== HOOKS

/**
 * The cue currently being dragged, if any.
 *
 * For the drag overlay and the dropzones, which need the cue itself to decide
 * what a drop would mean.
 */
export function useDraggingCue(): DraggingCue | null {
  return useStoreKeySelector(
    cueDndStore,
    'draggingCue',
    (draggingCue) => draggingCue,
  )
}

/**
 * Is this specific cue the one being dragged?
 *
 * Resolves to a boolean before React compares, so a row re-renders only when
 * its own drag state flips, not on every pointer gesture in the list.
 */
export function useIsDraggingCue(cueId: string): boolean {
  return useStoreKeySelector(
    cueDndStore,
    'draggingCue',
    (draggingCue) => draggingCue?.id === cueId,
  )
}

// #region ============== DEV
if (process.env.NODE_ENV !== 'production') {
  devtools(cueDndStore, { name: 'cueDndStore', enabled: true })
}
// #endregion
